import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  BidangKeahlianSwadayaDataType,
  PelatihanSwadayaDataType,
  PendidikanSwadayaDataType,
  SwadayaDataType,
  WilkerSwadayaDataType
} from './types'
import type {
  FilterSubitemSwadayaValues,
  FilterSwadayaValues,
  MutationBidangKeahlianSwadayaValues,
  MutationPelatihanSwadayaValues,
  MutationPendidikanSwadayaValues,
  MutationSwadayaValues
} from './validation'

interface InfiniteQueryOptions<T> {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (lastPage: ServiceGetAllResponse<T>) => number | undefined
  queryFn: QueryFunction<ServiceGetAllResponse<T>, QueryKey, number>
}

const SWADAYA_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['swadaya', key] as const

const getAll = (
  params?: FilterSwadayaValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<SwadayaDataType>>> => ({
  queryKey: SWADAYA_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<SwadayaDataType>>
      >('/ketenagaan/penyuluh/swadaya', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<SwadayaDataType>> => ({
  queryKey: SWADAYA_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<SwadayaDataType>>(
        `/ketenagaan/penyuluh/swadaya/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwadayaDataType>,
  MutationSwadayaValues
> => ({
  mutationFn: async (data: MutationSwadayaValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<SwadayaDataType>>(
        '/ketenagaan/penyuluh/swadaya',
        {
          ...data,
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          jenjang_pendidikan: data.jenjang_pendidikan?.value,
          bpp: data.bpp?.value,
          kecamatan: data.base_kecamatan.map(kecamatan => ({
            id: kecamatan.id_kecamatan.value,
            desa: kecamatan.id_desa.map(desa => desa.value)
          })),
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwadayaDataType>,
  MutationSwadayaValues
> => ({
  mutationFn: async (data: MutationSwadayaValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<SwadayaDataType>>(
        `/ketenagaan/penyuluh/swadaya/${data.id_swadaya}`,
        {
          ...data,
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          jenjang_pendidikan: data.jenjang_pendidikan?.value,
          bpp: data.bpp?.value,
          kecamatan: data.base_kecamatan.map(kecamatan => ({
            id: kecamatan.id_kecamatan.value,
            desa: kecamatan.id_desa.map(desa => desa.value)
          })),
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllSwadayaInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<SwadayaDataType[]> => ({
  queryKey: SWADAYA_QUERY_KEY({ ...params, type: 'swadaya' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<SwadayaDataType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<SwadayaDataType[]>>(
        `/ketenagaan/penyuluh/swadaya`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SwadayaDataType>,
  { id_poktan: string }
> => ({
  mutationFn: async (data: { id_poktan: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<SwadayaDataType>
      >(`/ketenagaan/penyuluh/swadaya/${data.id_poktan}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (filterParams?: FilterSwadayaValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/ketenagaan/penyuluh/swadaya/export`, {
        params: {
          ...filterParams,
          jenis: 'swadaya'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START Pelatihan Swadaya
const getAllPelatihanSwadaya = (
  params?: FilterSubitemSwadayaValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PelatihanSwadayaDataType>>
> => ({
  queryKey: SWADAYA_QUERY_KEY({ ...params, type: 'pelatihan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PelatihanSwadayaDataType>>
      >(`/ketenagaan/penyuluh/swadaya/${params?.id_swadaya}/pelatihan`, {
        params: {
          ...params,
          id_swadaya: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPelatihanSwadaya = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanSwadayaDataType>,
  MutationPelatihanSwadayaValues
> => ({
  mutationFn: async (data: MutationPelatihanSwadayaValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PelatihanSwadayaDataType>
      >(`/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pelatihan`, {
        ...data
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePelatihanSwadaya = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanSwadayaDataType>,
  MutationPelatihanSwadayaValues
> => ({
  mutationFn: async (data: MutationPelatihanSwadayaValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PelatihanSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pelatihan/${data.id_pelatihan}`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePelatihanSwadaya = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanSwadayaDataType>,
  { id_swadaya: string; id_data: string }
> => ({
  mutationFn: async (data: { id_swadaya: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PelatihanSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pelatihan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN SWADAYA
const getAllBidangKeahlian = (
  params?: FilterSubitemSwadayaValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<BidangKeahlianSwadayaDataType>>
> => ({
  queryKey: SWADAYA_QUERY_KEY({ ...params, type: 'bidang-keahlian' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<BidangKeahlianSwadayaDataType>>
      >(`/ketenagaan/penyuluh/swadaya/${params?.id_swadaya}/keahlian`, {
        params: {
          ...params,
          id_swadaya: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>,
  MutationBidangKeahlianSwadayaValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianSwadayaValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>
      >(`/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/keahlian`, {
        ...data,
        id_subsektor: data.subsektor.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>,
  MutationBidangKeahlianSwadayaValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianSwadayaValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/keahlian/${data.id_bidang_keahlian}`,
        {
          ...data,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>,
  { id_swadaya: string; id_data: string }
> => ({
  mutationFn: async (data: { id_swadaya: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<BidangKeahlianSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/keahlian/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN SWADAYA
const getAllPendidikan = (
  params?: FilterSubitemSwadayaValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PendidikanSwadayaDataType>>
> => ({
  queryKey: SWADAYA_QUERY_KEY({ ...params, type: 'pendidikan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PendidikanSwadayaDataType>>
      >(`/ketenagaan/penyuluh/swadaya/${params?.id_swadaya}/pendidikan`, {
        params: {
          ...params,
          id_swadaya: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanSwadayaDataType>,
  MutationPendidikanSwadayaValues
> => ({
  mutationFn: async (data: MutationPendidikanSwadayaValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PendidikanSwadayaDataType>
      >(`/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pendidikan`, {
        ...data,
        jenjang_pendidikan: data.jenjang_pendidikan.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanSwadayaDataType>,
  MutationPendidikanSwadayaValues
> => ({
  mutationFn: async (data: MutationPendidikanSwadayaValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PendidikanSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pendidikan/${data.id_pendidikan}`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanSwadayaDataType>,
  { id_swadaya: string; id_data: string }
> => ({
  mutationFn: async (data: { id_swadaya: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PendidikanSwadayaDataType>
      >(
        `/ketenagaan/penyuluh/swadaya/${data?.id_swadaya}/pendidikan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllWilayahKerja = (
  params?: FilterSubitemSwadayaValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
> => ({
  queryKey: SWADAYA_QUERY_KEY({ ...params, type: 'wilayah-kerja' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
      >(`/ketenagaan/penyuluh/swadaya/${params?.id_swadaya}/wilayah-kerja`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const swadaya = {
  queryKey: [SWADAYA_QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData,
  exportData,
  getAllSwadayaInfinite,

  getAllPelatihanSwadaya,
  createPelatihanSwadaya,
  updatePelatihanSwadaya,
  deletePelatihanSwadaya,

  getAllBidangKeahlian,
  createBidangKeahlian,
  updateBidangKeahlian,
  deleteBidangKeahlian,

  getAllPendidikan,
  createPendidikan,
  updatePendidikan,
  deletePendidikan,

  getAllWilayahKerja
}
